import type { UnitDef } from '../data/types';
import { MAX_QUEUE, isComplete, type Building } from '../entities/building';
import type { Player } from '../entities/player';
import type { Unit } from '../entities/unit';
import type { TilePoint } from '../map/pathfinding';

/** What the production system needs from the simulation. `Sim` satisfies this. */
export interface ProductionContext {
  player(owner: number): Player;
  supplyCap(owner: number): number;
  freeTileAround(b: Building, maxRing?: number): TilePoint | null;
  spawnUnit(owner: number, def: UnitDef, tileX: number, tileY: number): Unit;
}

export type TrainRefusal =
  'not-built' | 'cannot-train' | 'queue-full' | 'no-gold' | 'no-lumber' | 'no-supply';

/** Why a building cannot start training `def` right now, or null when it can. Used by the UI too. */
export function trainRefusal(
  ctx: ProductionContext,
  b: Building,
  def: UnitDef,
): TrainRefusal | null {
  if (!isComplete(b)) return 'not-built';
  if (!b.def.trains.includes(def.id)) return 'cannot-train';
  if (b.queue.length >= MAX_QUEUE) return 'queue-full';
  const p = ctx.player(b.owner);
  if (p.gold < def.cost.gold) return 'no-gold';
  if (p.lumber < def.cost.lumber) return 'no-lumber';
  if (p.supplyUsed + def.supply > ctx.supplyCap(b.owner)) return 'no-supply';
  return null;
}

/** Pay up front and queue. Returns the refusal reason, or null on success. */
export function enqueueTrain(
  ctx: ProductionContext,
  b: Building,
  def: UnitDef,
): TrainRefusal | null {
  const refusal = trainRefusal(ctx, b, def);
  if (refusal) return refusal;
  const p = ctx.player(b.owner);
  p.gold -= def.cost.gold;
  p.lumber -= def.cost.lumber;
  p.supplyUsed += def.supply;
  b.queue.push({ def, ticksLeft: def.trainTicks });
  return null;
}

/** Remove the last queued entry and refund it. Returns false when the queue is empty. */
export function cancelLastTrain(ctx: ProductionContext, b: Building): boolean {
  const entry = b.queue.pop();
  if (!entry) return false;
  const p = ctx.player(b.owner);
  p.gold += entry.def.cost.gold;
  p.lumber += entry.def.cost.lumber;
  p.supplyUsed -= entry.def.supply;
  return true;
}

/**
 * Advance the front of the queue by one tick. A finished unit steps out onto a free tile
 * touching the building (never further, so it cannot appear beyond a wall); if none is
 * free it waits, retrying every tick.
 */
export function stepProduction(ctx: ProductionContext, b: Building): void {
  const front = b.queue[0];
  if (!front) return;
  if (front.ticksLeft > 0) front.ticksLeft--;
  if (front.ticksLeft > 0) return;
  const tile = ctx.freeTileAround(b, 1);
  if (!tile) return; // boxed in or crowded; try again next tick
  b.queue.shift();
  ctx.spawnUnit(b.owner, front.def, tile.x, tile.y);
}
